<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportMetricCard from '../ReportMetricCard.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  summary: {
    type: Object,
    default: null,
  },
  previousSummary: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const formatTime = seconds => {
  if (!seconds) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const firstResponseTime = computed(() =>
  formatTime(props.summary?.avg_first_response_time)
);

const resolutionTime = computed(() =>
  formatTime(props.summary?.avg_resolution_time)
);

const firstResponseTrend = computed(() =>
  calculateTrend(
    props.summary?.avg_first_response_time,
    props.previousSummary?.avg_first_response_time
  )
);

const resolutionTrend = computed(() =>
  calculateTrend(
    props.summary?.avg_resolution_time,
    props.previousSummary?.avg_resolution_time
  )
);
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11">
      {{ t('CEO_DASHBOARD.RESPONSE_TIMES.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div v-else class="grid grid-cols-2 gap-6">
      <ReportMetricCard
        :label="t('CEO_DASHBOARD.RESPONSE_TIMES.FIRST_RESPONSE')"
        :info-text="t('CEO_DASHBOARD.RESPONSE_TIMES.FIRST_RESPONSE_TOOLTIP')"
        :value="firstResponseTime"
        :trend="firstResponseTrend"
        invert-trend
      />
      <ReportMetricCard
        :label="t('CEO_DASHBOARD.RESPONSE_TIMES.RESOLUTION')"
        :info-text="t('CEO_DASHBOARD.RESPONSE_TIMES.RESOLUTION_TOOLTIP')"
        :value="resolutionTime"
        :trend="resolutionTrend"
        invert-trend
      />
    </div>
  </div>
</template>
